import { apiClient } from './client';
import type {
  GoalTemplate,
  GoalInstanceWithDetails,
  CreateGoalTemplateDto,
  UpdateGoalTemplateDto,
  CreateGoalInstanceDto,
  GoalEntityType,
  GoalProgress,
  InitializeGoalInstanceResult,
} from '@/types/goal.types';

export class GoalService {
  // Goal Templates (API still uses /definitions path)
  async getDefinitions(): Promise<GoalTemplate[]> {
    const response = await apiClient.get('/goals/definitions');
    return response.data.data || [];
  }

  async getDefinitionsByEntityType(entityType: GoalEntityType): Promise<GoalTemplate[]> {
    const response = await apiClient.get(`/goals/definitions?entityType=${entityType}`);
    return response.data.data || [];
  }

  async getDefinitionById(id: number): Promise<GoalTemplate> {
    const response = await apiClient.get(`/goals/definitions/${id}`);
    return response.data.data;
  }

  async createDefinition(data: CreateGoalTemplateDto): Promise<GoalTemplate> {
    const response = await apiClient.post('/goals/definitions', data);
    return response.data.data;
  }

  async updateDefinition(id: number, data: UpdateGoalTemplateDto): Promise<GoalTemplate> {
    const response = await apiClient.put(`/goals/definitions/${id}`, data);
    return response.data.data;
  }

  async deleteDefinition(id: number): Promise<void> {
    await apiClient.delete(`/goals/definitions/${id}`);
  }

  // Goal Instances
  async getInstancesByEntity(entityType: GoalEntityType, entityId: number): Promise<GoalInstanceWithDetails[]> {
    const response = await apiClient.get(`/goals/instances/entity/${entityType}/${entityId}`);
    return response.data.data || [];
  }

  async getInstanceById(id: number): Promise<GoalInstanceWithDetails> {
    const response = await apiClient.get(`/goals/instances/${id}`);
    return response.data.data;
  }

  async createInstance(data: CreateGoalInstanceDto): Promise<GoalInstanceWithDetails> {
    const response = await apiClient.post('/goals/instances', data);
    return response.data.data;
  }

  async initializeInstance(id: number): Promise<InitializeGoalInstanceResult> {
    const response = await apiClient.post(`/goals/instances/${id}/initialize`);
    return response.data.data;
  }

  async deleteInstance(id: number, deleteTasks = false): Promise<void> {
    await apiClient.delete(`/goals/instances/${id}?deleteTasks=${deleteTasks}`);
  }

  async getInstanceProgress(id: number): Promise<GoalProgress> {
    const response = await apiClient.get(`/goals/instances/${id}/progress`);
    return response.data.data;
  }

  // Get available goals for an entity (not yet attached)
  async getAvailableGoals(entityType: GoalEntityType, entityId: number): Promise<GoalTemplate[]> {
    const response = await apiClient.get(`/goals/instances/available/${entityType}/${entityId}`);
    return response.data.data || [];
  }
}

export const goalService = new GoalService();
